// src/studio/avatar/services/studioAnalyticsApi.js

import api from '../../../api';

const EVENTS_PATH = '/studio/analytics/events';

const CLIENT_EVENTS = new Set([
  'studio_opened',
  'avatar_customization_started',
  'avatar_new_started',
  'avatar_edit_opened',
  'studio_entry_clicked',
  'avatar_post_composer_opened',
]);


const ENTRY_POINTS = new Set([
  'edit_profile',
  'profile',
  'user_profile',
  'post_fullscreen',
  'home_awareness',
  'unknown',
]);


export function createStudioAnalyticsId() {
  if (
    typeof window !== 'undefined' &&
    typeof window.crypto?.randomUUID === 'function'
  ) {
    return window.crypto.randomUUID();
  }

  return `studio_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2)}_${Math.random().toString(36).slice(2)}`;
}

export function normalizeStudioEntryPoint(value) {
  return ENTRY_POINTS.has(value) ? value : 'unknown';
}

// Create once when entering Studio and retain for that visit.
// Switching categories or opening My Creations keeps the same visit.
export function createStudioVisit(entryPoint = 'unknown') {
  return Object.freeze({
    visitId: createStudioAnalyticsId(),
    entryPoint: normalizeStudioEntryPoint(entryPoint),
  });
}

/**
 * Create an event once for a logical action.
 * Retrying must reuse this returned object and its eventId.
 *
 * Returns null for an unsupported event.
 */
export function createStudioAnalyticsEvent(
  eventType,
  {
    visitId,
    entryPoint = 'unknown',
    creationId,
    studioImageCount,
  } = {}
) {
  if (!CLIENT_EVENTS.has(eventType)) {
    return null;
  }

  return Object.freeze({
    eventType,
    eventId: createStudioAnalyticsId(),
    entryPoint: normalizeStudioEntryPoint(entryPoint),

    ...(visitId !== undefined && visitId !== null
      ? { visitId }
      : {}),

    ...(eventType === 'avatar_edit_opened' &&
    creationId !== undefined
      ? { creationId }
      : {}),

    ...(eventType === 'avatar_post_composer_opened' &&
    studioImageCount !== undefined
      ? { studioImageCount }
      : {}),
  });
}

/**
 * Send an event without throwing or displaying an error.
 *
 * Call without awaiting it in navigation handlers:
 *   void trackStudioEvent(event);
 *
 * Authentication is handled by the existing api instance.
 * No username, timestamp or backend outcome is submitted.
 */
export async function trackStudioEvent(event) {
  try {
    if (
      !event ||
      !CLIENT_EVENTS.has(event.eventType) ||
      typeof event.eventId !== 'string'
    ) {
      return false;
    }

    // Explicit fields prevent accidental submission of other data.
    const payload = {
      eventType: event.eventType,
      eventId: event.eventId,
      entryPoint: normalizeStudioEntryPoint(event.entryPoint),

      ...(event.visitId !== undefined
        ? { visitId: event.visitId }
        : {}),

      ...(event.eventType === 'avatar_edit_opened' &&
      event.creationId !== undefined
        ? { creationId: event.creationId }
        : {}),

      ...(event.eventType === 'avatar_post_composer_opened' &&
      event.studioImageCount !== undefined
        ? { studioImageCount: event.studioImageCount }
        : {}),
    };

    const { data } = await api.post(EVENTS_PATH, payload, {
      timeout: 5000,
    });

    return data?.recorded === true || data?.duplicate === true;
  } catch {
    // Tracking must never interrupt the user flow.
    return false;
  }
}
